import {AvailabilityLabels} from '@/enums/AvailabilityLabels';

/**
 * Pure layout of the availability card (label filter + mini week timeline + "Zuletzt aktualisiert"), checked with
 * tests/availability/availabilityCard.check.ts. No Vue, no API.
 * Colours follow the availability bands of TimetableV2 (availabilityBands.ts / CalTimeGridView): course windows green,
 * Nachholtermine purple; the other labels get their own distinct tones.
 */

export interface CardAvailability {
    dayOfWeek: number;
    timeRange: { startTime: number; endTime: number };
    labels?: string[];
    updatedAt?: Date | string;
    updatedBy?: unknown;
}

export interface LabelMeta {
    value: AvailabilityLabels;
    /** Short name of the filter pill */
    short: string;
    /** Full name (tooltip, details) */
    text: string;
    color: string;
}

/** Canonical order = order of the filter pills and priority of the bar colour */
export const LABEL_META: LabelMeta[] = [
    {value: AvailabilityLabels.FOR_COURSES, short: 'Kurse', text: 'Für Kurse', color: '#43A047'},
    {value: AvailabilityLabels.FOR_HANDOUT_EVENTS, short: 'Austeilaktionen', text: 'Für Austeilaktionen', color: '#FB8C00'},
    {value: AvailabilityLabels.FOR_ONLINE_COURSES, short: 'Online', text: 'Für Online-Kurse', color: '#1E88E5'},
    {value: AvailabilityLabels.EXCEPTIONAL, short: 'Ausnahme', text: 'In Ausnahmefällen', color: '#8D6E63'},
    {value: AvailabilityLabels.FOR_MAKEUP, short: 'Nachholen', text: 'Für Nachholtermine', color: '#8E24AA'},
];

/** Windows without (known) label: neutral grey ("ohne Label"), never the green of "Kurse" */
export const NEUTRAL_COLOR = '#90A4AE';
export const NO_LABEL_TEXT = 'ohne Label';

export type LabelFilter = 'all' | AvailabilityLabels;

export function labelMeta(label: string): LabelMeta | undefined {
    return LABEL_META.find((meta) => meta.value === label);
}

export function labelText(label: string): string {
    return labelMeta(label)?.text || 'Unbekannt';
}

/** Labels that occur in the list, in canonical order (the filter shows only these) */
export function presentLabels(items: CardAvailability[]): AvailabilityLabels[] {
    const used = new Set<string>();
    items.forEach((item) => (item.labels || []).forEach((label) => used.add(label)));
    return LABEL_META.filter((meta) => used.has(meta.value)).map((meta) => meta.value);
}

/** Default like before: "Kurse" when there are course windows, otherwise all */
export function defaultFilter(items: CardAvailability[]): LabelFilter {
    return presentLabels(items).includes(AvailabilityLabels.FOR_COURSES) ? AvailabilityLabels.FOR_COURSES : 'all';
}

/** A filter whose label vanished (e.g. after Terminabgleich / saving) falls back to all */
export function validFilter(filter: LabelFilter, items: CardAvailability[]): LabelFilter {
    return filter === 'all' || presentLabels(items).includes(filter) ? filter : 'all';
}

/**
 * Same rule as the former chip filter: a label shows its windows; "In Ausnahmefällen" windows only when that label is
 * selected itself (or "Alle")
 */
export function filterAvailabilities<T extends CardAvailability>(items: T[], filter: LabelFilter): T[] {
    if (filter === 'all') {
        return items;
    }
    return items.filter((item) => {
        const labels = item.labels || [];
        return labels.includes(filter) && (filter === AvailabilityLabels.EXCEPTIONAL || !labels.includes(AvailabilityLabels.EXCEPTIONAL));
    });
}

export function clock(minutes: number): string {
    const value = Math.max(0, Math.min(24 * 60, Math.round(minutes)));
    return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
}

/** "15–18" / "15:30–18" for narrow bars */
export function shortClock(minutes: number): string {
    return minutes % 60 ? clock(minutes) : String(Math.floor(minutes / 60));
}

const DAYS = [
    {value: 0, short: 'Mo', text: 'Montag'},
    {value: 1, short: 'Di', text: 'Dienstag'},
    {value: 2, short: 'Mi', text: 'Mittwoch'},
    {value: 3, short: 'Do', text: 'Donnerstag'},
    {value: 4, short: 'Fr', text: 'Freitag'},
    {value: 5, short: 'Sa', text: 'Samstag'},
    {value: 6, short: 'So', text: 'Sonntag'},
];

/** Weekdays enum of the app: 0 = Montag … 6 = Sonntag; Date#getDay: 0 = Sonntag */
export function weekdayOf(date: Date): number {
    return (date.getDay() + 6) % 7;
}

export interface TimelineBar {
    key: string;
    start: number;
    end: number;
    /** Position in % of the axis */
    left: number;
    width: number;
    /** Overlapping windows of one day are drawn in separate lanes */
    lane: number;
    /** Main colour (tint): the selected label, else the first label in canonical order, else neutral */
    color: string;
    /** Colours of the segmented left edge: all labels under "Alle", only the main colour under a label filter */
    edgeColors: string[];
    /** All labels of the window in canonical order (chips with text in "Alle" and in the pinned details) */
    chips: LabelMeta[];
    /** Window has no (known) label: neutral "ohne Label" style */
    unlabeled: boolean;
    labels: string[];
    time: string;
    shortTime: string;
    /** Where the time text goes: inside the bar, beside it when the bar is too narrow, 'none' when there is no free room
     * (no overlap with neighbours - the details stay available as tooltip / pinned line) */
    textAt: 'inside' | 'after' | 'before' | 'none';
    /** Text shown on / beside the bar (full or short time) */
    text: string;
    details: string;
}

export interface TimelineRow {
    day: number;
    short: string;
    text: string;
    isToday: boolean;
    empty: boolean;
    lanes: number;
    bars: TimelineBar[];
    /** Text form of the day (phone list, screen readers): "15:00–18:00, 19:00–20:00" */
    summary: string;
}

export interface Timeline {
    startHour: number;
    endHour: number;
    ticks: Array<{ hour: number; left: number }>;
    rows: TimelineRow[];
}

export interface TimelineOptions {
    /** Weekday (0 = Montag) to highlight, undefined = none */
    today?: number;
    /** Selected label: colours bars by it first */
    filter?: LabelFilter;
    /** Windows the axis fits to (e.g. all windows before filtering, so the axis does not jump between filters) */
    axisItems?: CardAvailability[];
    /** Axis used without data and minimum span */
    defaultStartHour?: number;
    defaultEndHour?: number;
    minSpanHours?: number;
    /** Measured width of the bar track in px (text fitting); default 400 */
    trackWidthPx?: number;
    /** false: the data has no labels at all (Schüler:innen) - no "ohne Label" texts, only the neutral colour */
    labelsUsed?: boolean;
}

/** Approximate text metrics of the 11px tabular time text */
const CHAR_PX = 6.2;
const INSIDE_PAD_PX = 14;
const OUTSIDE_GAP_PX = 4;

const textPx = (text: string) => text.length * CHAR_PX;

/**
 * Places the time text of the bars of one lane (sorted by start): inside when it fits (full time, else short time),
 * otherwise after / before the bar if that room is free of other bars and of already placed texts, else hidden.
 */
export function placeTexts(bars: TimelineBar[], trackWidthPx: number): void {
    const pxToPercent = (px: number) => (px / Math.max(1, trackWidthPx)) * 100;
    const taken: Array<[number, number]> = bars.map((bar) => [bar.left, bar.left + bar.width]);
    const free = (from: number, to: number) => from >= 0 && to <= 100 && taken.every(([a, b]) => to <= a || from >= b);
    bars.forEach((bar) => {
        const widthPx = (bar.width / 100) * trackWidthPx;
        if (widthPx >= textPx(bar.time) + INSIDE_PAD_PX) {
            bar.textAt = 'inside';
            bar.text = bar.time;
            return;
        }
        if (widthPx >= textPx(bar.shortTime) + INSIDE_PAD_PX) {
            bar.textAt = 'inside';
            bar.text = bar.shortTime;
            return;
        }
        bar.text = bar.shortTime;
        const need = pxToPercent(textPx(bar.shortTime) + OUTSIDE_GAP_PX);
        const end = bar.left + bar.width;
        if (free(end, end + need)) {
            bar.textAt = 'after';
            taken.push([end, end + need]);
        } else if (free(bar.left - need, bar.left)) {
            bar.textAt = 'before';
            taken.push([bar.left - need, bar.left]);
        } else {
            bar.textAt = 'none';
        }
    });
}

function validItems(items: CardAvailability[]): CardAvailability[] {
    return items.filter((item) => item && item.timeRange && item.timeRange.endTime > item.timeRange.startTime
        && item.dayOfWeek >= 0 && item.dayOfWeek <= 6);
}

/** Axis auto-fits the data (whole hours), at least minSpanHours wide, within 0–24 */
export function timelineAxis(items: CardAvailability[], options: TimelineOptions = {}): { startHour: number; endHour: number } {
    const defStart = options.defaultStartHour ?? 8;
    const defEnd = options.defaultEndHour ?? 18;
    const minSpan = options.minSpanHours ?? 6;
    const valid = validItems(items);
    if (!valid.length) {
        return {startHour: defStart, endHour: defEnd};
    }
    let startHour = Math.floor(Math.min(...valid.map((item) => item.timeRange.startTime)) / 60);
    let endHour = Math.ceil(Math.max(...valid.map((item) => item.timeRange.endTime)) / 60);
    while (endHour - startHour < minSpan) {
        if (endHour < 24) {
            endHour++;
        }
        if (endHour - startHour < minSpan && startHour > 0) {
            startHour--;
        }
        if (startHour === 0 && endHour === 24) {
            break;
        }
    }
    return {startHour, endHour};
}

function axisTicks(startHour: number, endHour: number): Array<{ hour: number; left: number }> {
    const span = endHour - startHour;
    const step = span <= 8 ? 1 : span <= 14 ? 2 : 3;
    const ticks: Array<{ hour: number; left: number }> = [];
    for (let hour = Math.ceil(startHour / step) * step; hour <= endHour; hour += step) {
        ticks.push({hour, left: ((hour - startHour) / span) * 100});
    }
    return ticks;
}

/** "Alle": every label of the window is visible (segmented edge + chips); a label filter keeps the single colour (edge) */
export function barColors(labels: string[], filter: LabelFilter | undefined)
    : { color: string; edgeColors: string[]; chips: LabelMeta[]; unlabeled: boolean } {
    const ordered = LABEL_META.filter((meta) => labels.includes(meta.value));
    if (!ordered.length) {
        return {color: NEUTRAL_COLOR, edgeColors: [NEUTRAL_COLOR], chips: [], unlabeled: true};
    }
    const selected = filter && filter !== 'all' ? ordered.find((meta) => meta.value === filter) : undefined;
    if (selected) {
        return {color: selected.color, edgeColors: [selected.color], chips: ordered, unlabeled: false};
    }
    return {color: ordered[0].color, edgeColors: ordered.map((meta) => meta.color), chips: ordered, unlabeled: false};
}

/** CSS background of the segmented left edge (equal vertical segments per colour) */
export function edgeBackground(colors: string[]): string {
    if (colors.length <= 1) {
        return colors[0] || NEUTRAL_COLOR;
    }
    const step = 100 / colors.length;
    const stops = colors.map((color, i) => `${color} ${(i * step).toFixed(2)}% ${((i + 1) * step).toFixed(2)}%`);
    return `linear-gradient(to bottom, ${stops.join(', ')})`;
}

/** Greedy lanes: a window starts a new lane only when it overlaps one of the earlier lanes */
function assignLanes(items: CardAvailability[]): number[] {
    const laneEnds: number[] = [];
    return items.map((item) => {
        let lane = laneEnds.findIndex((end) => end <= item.timeRange.startTime);
        if (lane === -1) {
            lane = laneEnds.length;
            laneEnds.push(item.timeRange.endTime);
        } else {
            laneEnds[lane] = item.timeRange.endTime;
        }
        return lane;
    });
}

/** Rows Mo–Fr always, Sa/So only when they have windows; bars positioned on the shared hour axis */
export function buildTimeline(items: CardAvailability[], options: TimelineOptions = {}): Timeline {
    const valid = validItems(items);
    const {startHour, endHour} = timelineAxis(options.axisItems ? [...validItems(options.axisItems), ...valid] : valid, options);
    const axisStart = startHour * 60;
    const span = (endHour - startHour) * 60;
    const rows = DAYS
        .map((day) => {
            const dayItems = valid.filter((item) => item.dayOfWeek === day.value)
                .sort((a, b) => a.timeRange.startTime - b.timeRange.startTime || a.timeRange.endTime - b.timeRange.endTime);
            const lanes = assignLanes(dayItems);
            const bars: TimelineBar[] = dayItems.map((item, index) => {
                const start = item.timeRange.startTime;
                const end = item.timeRange.endTime;
                const left = ((start - axisStart) / span) * 100;
                const width = ((end - start) / span) * 100;
                const labels = item.labels || [];
                const colors = barColors(labels, options.filter);
                const time = `${clock(start)}–${clock(end)}`;
                return {
                    key: `${day.value}-${index}`,
                    start, end, left, width,
                    lane: lanes[index],
                    ...colors,
                    labels,
                    time,
                    shortTime: `${shortClock(start)}–${shortClock(end)}`,
                    textAt: 'none',
                    text: '',
                    details: [`${day.text} · ${time} Uhr`,
                        ...(colors.unlabeled ? (options.labelsUsed === false ? [] : [NO_LABEL_TEXT]) : colors.chips.map((chip) => chip.text)),
                    ].join(' · '),
                };
            });
            const laneCount = Math.max(1, ...bars.map((bar) => bar.lane + 1));
            for (let lane = 0; lane < laneCount; lane++) {
                placeTexts(bars.filter((bar) => bar.lane === lane), options.trackWidthPx ?? 400);
            }
            return {
                day: day.value,
                short: day.short,
                text: day.text,
                isToday: options.today === day.value,
                empty: !bars.length,
                lanes: laneCount,
                bars,
                summary: bars.map((bar) => bar.time).join(', ') || '–',
            } as TimelineRow;
        })
        .filter((row) => !row.empty || row.day < 5);
    return {startHour, endHour, ticks: axisTicks(startHour, endHour), rows};
}

function idOf(value: unknown): string | undefined {
    if (!value) {
        return undefined;
    }
    if (typeof value === 'object' && (value as { _id?: unknown })._id) {
        return String((value as { _id: unknown })._id);
    }
    return String(value);
}

/** Latest updatedAt of the stored windows and who saved that window (updatedBy is optional: older entries have none) */
export function lastUpdate(items: CardAvailability[] | null | undefined): { date: Date; userId?: string } | undefined {
    let latest: { date: Date; userId?: string } | undefined;
    (items || []).forEach((item) => {
        if (!item || !item.updatedAt) {
            return;
        }
        const date = new Date(item.updatedAt);
        if (isNaN(date.valueOf())) {
            return;
        }
        if (!latest || date.valueOf() > latest.date.valueOf()) {
            latest = {date, userId: idOf(item.updatedBy)};
        } else if (date.valueOf() === latest.date.valueOf() && !latest.userId) {
            latest.userId = idOf(item.updatedBy);
        }
    });
    return latest;
}

/** "Max Muster" from a populated login user meta; empty when there is no name (never falls back to the mail) */
export function editorName(loginUser: { meta?: unknown } | null | undefined): string {
    const meta = loginUser && loginUser.meta && typeof loginUser.meta === 'object'
        ? loginUser.meta as { firstName?: string; lastName?: string; name?: string } : undefined;
    if (!meta) {
        return '';
    }
    return `${meta.firstName || ''} ${meta.lastName || ''}`.trim() || meta.name || '';
}
